import { getSupabase, isSupabaseConfigured } from "./supabase";

const TOKEN_KEY = "cf_admin_token";

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function notifyUnreadRefresh() {
  window.dispatchEvent(new Event("cf-unread-refresh"));
}

function assertConfigured() {
  if (!isSupabaseConfigured) {
    throw new Error("Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in frontend/.env");
  }
}

async function rpc(fn, args = {}) {
  assertConfigured();
  const { data, error } = await getSupabase().rpc(fn, args);
  if (error) throw new Error(error.message);
  return data;
}

export const adminApi = {
  login: (username, password) => rpc("admin_login", { p_username: username, p_password: password }),
  me: (token) => rpc("admin_me", { p_token: token }),
  logout: (token) => rpc("admin_logout", { p_token: token }),
  listAdmins: (token) => rpc("admin_list", { p_token: token }),
  createAdmin: (token, payload) =>
    rpc("admin_create", {
      p_token: token,
      p_username: payload.username,
      p_password: payload.password,
      p_email: payload.email || "",
      p_role: payload.role || "admin",
      p_permissions: payload.role === "superadmin" ? {} : payload.permissions || {},
    }),
  updateAdminPermissions: (token, id, permissions) =>
    rpc("admin_update_permissions", {
      p_token: token,
      p_admin_id: id,
      p_permissions: permissions,
    }),
  setAdminPassword: (token, id, password) =>
    rpc("admin_set_password", {
      p_token: token,
      p_admin_id: id,
      p_password: password,
    }),
  setAdminActive: (token, id, isActive) =>
    rpc("admin_set_active", { p_token: token, p_admin_id: id, p_is_active: isActive }),
  listContacts: (token) => rpc("admin_list_contacts", { p_token: token }),
  listContactMessages: (token, id) =>
    rpc("admin_list_contact_messages", { p_token: token, p_id: id }),
  replyContact: (token, id, body) =>
    rpc("admin_reply_contact", { p_token: token, p_id: id, p_body: body }),
  markContactMessageEmailed: (token, id) =>
    rpc("admin_mark_thread_message_emailed", { p_token: token, p_id: id }),
  updateContact: (token, id, status, emailSent) =>
    rpc("admin_update_contact", {
      p_token: token,
      p_id: id,
      p_status: status,
      p_email_sent: emailSent ?? null,
    }),
  visitStats: (token) => rpc("admin_visit_stats", { p_token: token }),
  listVisits: (token, limit = 100) => rpc("admin_list_visits", { p_token: token, p_limit: limit }),
  upsertContent: (token, key, value) =>
    rpc("admin_upsert_content", { p_token: token, p_key: key, p_value: value }),
  uploadVideo: async (file) => {
    assertConfigured();
    const maxBytes = 80 * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error("Please choose a video under 80MB, or paste a YouTube / file URL instead.");
    }
    const ext = (file.name.split(".").pop() || "mp4").toLowerCase().replace(/[^a-z0-9]/g, "") || "mp4";
    const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await getSupabase().storage.from("videos").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || "video/mp4",
    });
    if (error) {
      throw new Error(
        error.message.includes("Bucket not found")
          ? "Video storage is not set up yet. Run supabase/migrations/20260827_videos.sql in the Supabase SQL Editor."
          : error.message
      );
    }
    const { data } = getSupabase().storage.from("videos").getPublicUrl(path);
    return data.publicUrl;
  },
  uploadImage: async (file) => {
    assertConfigured();
    const maxBytes = 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error("Please choose an image under 8MB, or paste an image URL instead.");
    }
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
    const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await getSupabase().storage.from("images").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || "image/jpeg",
    });
    if (error) {
      throw new Error(
        error.message.includes("Bucket not found")
          ? "Image storage is not set up yet. Run supabase/migrations/20260904_images.sql in the Supabase SQL Editor."
          : error.message
      );
    }
    const { data } = getSupabase().storage.from("images").getPublicUrl(path);
    return data.publicUrl;
  },
  uploadImages: async (files) => {
    const list = Array.from(files || []);
    const urls = [];
    for (const file of list) {
      urls.push(await adminApi.uploadImage(file));
    }
    return urls;
  },
};

export async function fetchSiteContent() {
  if (!isSupabaseConfigured) return {};
  const { data, error } = await getSupabase().from("site_content").select("key, value");
  if (error) throw error;
  return Object.fromEntries((data || []).map((row) => [row.key, row.value]));
}

export async function trackPageVisit({ path, referrer, userAgent, visitorId, sessionId }) {
  if (!isSupabaseConfigured) return;
  const { error } = await getSupabase().from("page_visits").insert({
    path,
    referrer: referrer || null,
    user_agent: userAgent || null,
    visitor_id: visitorId || null,
    session_id: sessionId || null,
  });
  if (error) console.warn("Visit tracking failed:", error.message);
}

export async function submitContact({ name, email, subject, message }) {
  assertConfigured();
  const id = await rpc("submit_contact", {
    p_name: name,
    p_email: email,
    p_subject: subject,
    p_message: message,
  });
  return { id };
}
